// Cookie consent state for the marketing site.
//
// This module owns *only* the visitor's decision — whether analytics cookies
// may be set. It knows nothing about Google Analytics; googleAnalytics.ts
// subscribes to it and translates a decision into a Consent Mode update.
//
// Storage note: the choice itself is kept in localStorage, which is first-party
// and strictly necessary to honour the visitor's own preference. Storing "this
// person said no" is explicitly permitted without consent under PECR/ePrivacy —
// the alternative is asking again on every page load.

export type ConsentChoice = 'granted' | 'denied';

const STORAGE_KEY = 'pp_cookie_consent';

// Bump this when the categories of cookies change materially. An older stored
// decision is then treated as absent and the banner is shown again, because a
// visitor cannot meaningfully have consented to something that did not exist
// when they clicked.
const CONSENT_VERSION = 1;

// Consent is not indefinite. The ICO and most EU DPAs expect it to be refreshed
// periodically; 12 months is the common reading.
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

interface StoredConsent {
    version: number;
    choice: ConsentChoice;
    decidedAt: number;
}

// `null` means "there is no decision any more" — the visitor withdrew consent
// via the footer and the banner should reappear. Subscribers that only care
// about storage (Google Analytics) treat it exactly like 'denied'; the banner
// treats it as "ask again".
type ConsentListener = (choice: ConsentChoice | null) => void;

const listeners = new Set<ConsentListener>();

/**
 * The visitor's stored decision, or null if they have not made one (or the
 * stored one has expired / is from an older consent version).
 *
 * Every localStorage access here is wrapped: Safari in private mode and
 * browsers configured to block site data throw on access rather than returning
 * null, and an exception here must never take the page down.
 */
export function getStoredConsent(): ConsentChoice | null {
    let raw: string | null = null;
    try {
        raw = window.localStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
    if (!raw) {
        return null;
    }

    let parsed: StoredConsent;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }

    if (!parsed || parsed.version !== CONSENT_VERSION) {
        return null;
    }
    if (parsed.choice !== 'granted' && parsed.choice !== 'denied') {
        return null;
    }
    if (!parsed.decidedAt || Date.now() - parsed.decidedAt > CONSENT_MAX_AGE_MS) {
        return null;
    }
    return parsed.choice;
}

/**
 * Record the visitor's decision and notify subscribers.
 *
 * Subscribers are notified even when the write fails, so that a visitor who has
 * blocked storage entirely still gets the behaviour they asked for for the rest
 * of this page view — they will simply be asked again next time.
 */
export function setConsent(choice: ConsentChoice): void {
    const record: StoredConsent = {
        version: CONSENT_VERSION,
        choice,
        decidedAt: Date.now(),
    };
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
        // Ignore — see above.
    }
    listeners.forEach((listener) => listener(choice));
}

/**
 * Forget the stored decision so the banner reappears. Backs the "Cookie
 * Settings" link in the footer — visitors must be able to withdraw consent as
 * easily as they gave it.
 */
export function clearConsent(): void {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Ignore — see above.
    }
    // Withdrawal must take effect immediately, not on the next page load.
    listeners.forEach((listener) => listener(null));
}

/** Subscribe to decisions. Returns an unsubscribe function. */
export function onConsentChange(listener: ConsentListener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
